import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Filesystem } from "@capacitor/filesystem";
import { detectKind } from "./file";

type IncomingPayload = {
  name: string;
  mimeType?: string;
  uri?: string;
  data: string;
};

type IncomingFilePlugin = {
  consume(): Promise<IncomingPayload | undefined>;
  addListener(
    eventName: "incomingFile",
    listenerFunc: (data: {
      uri?: string;
      name?: string;
      mimeType?: string;
    }) => void
  ): Promise<PluginListenerHandle>;
};

const IncomingFile = registerPlugin<IncomingFilePlugin>("IncomingFile");

/**
 * Decode base64 from Android Base64.NO_WRAP into a fresh ArrayBuffer.
 * Avoids detached-buffer issues when the same bytes are passed to pdf.js.
 */
export function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const cleaned = b64.replace(/\s/g, "");
  const binary = atob(cleaned);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function nameFromUri(uri: string, mimeType?: string): string {
  try {
    const decoded = decodeURIComponent(uri);
    const seg = decoded.split(/[/:]/).pop() || "document";
    if (seg.includes(".")) return seg;
    if (mimeType === "application/pdf") return `${seg}.pdf`;
    if (mimeType === "application/msword") return `${seg}.doc`;
    if (
      mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return `${seg}.docx`;
    }
    return seg;
  } catch {
    return "document";
  }
}

function ensureExtension(
  name: string,
  mimeType: string | undefined,
  buffer: ArrayBuffer
): string {
  if (name.includes(".")) return name;
  const kind = detectKind(name, mimeType, buffer);
  if (kind === "pdf") return `${name}.pdf`;
  if (kind === "docx") return `${name}.docx`;
  if (kind === "doc") return `${name}.doc`;
  return name;
}

function isLikelyDocumentUri(url: string): boolean {
  const lower = url.toLowerCase();
  if (lower.startsWith("content://") || lower.startsWith("file://")) return true;
  return /\.(pdf|docx?|DOCX?)($|\?)/.test(url);
}

async function openFromFilesystemUri(
  url: string,
  onOpen: (
    name: string,
    buffer: ArrayBuffer,
    mimeType?: string,
    fileUri?: string
  ) => Promise<void>
): Promise<boolean> {
  try {
    const result = await Filesystem.readFile({ path: url });
    const data = result.data;
    if (typeof data !== "string" || !data) return false;
    const buffer = base64ToArrayBuffer(data);
    const name = ensureExtension(nameFromUri(url), undefined, buffer);
    await onOpen(name, buffer, undefined, url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Wire Android VIEW / SEND intents into the reader.
 * Uses the local IncomingFile Capacitor plugin when available; falls back to
 * App.getLaunchUrl + Filesystem content:// reads.
 */
export async function wireIncomingFileOpener(
  onOpen: (
    name: string,
    buffer: ArrayBuffer,
    mimeType?: string,
    fileUri?: string
  ) => Promise<void>
): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) {
    return () => undefined;
  }

  const handles: PluginListenerHandle[] = [];
  let openedOnce = false;

  const openPayload = async (payload: IncomingPayload) => {
    openedOnce = true;
    const buffer = base64ToArrayBuffer(payload.data);
    const name = ensureExtension(
      payload.name || "document",
      payload.mimeType,
      buffer
    );
    await onOpen(name, buffer, payload.mimeType, payload.uri);
  };

  if (Capacitor.isPluginAvailable("IncomingFile")) {
    try {
      const pending = await IncomingFile.consume();
      if (pending?.data) await openPayload(pending);
    } catch {
      /* ignore cold-start race */
    }

    handles.push(
      await IncomingFile.addListener("incomingFile", () => {
        void (async () => {
          try {
            const next = await IncomingFile.consume();
            if (next?.data) await openPayload(next);
          } catch {
            /* ignore */
          }
        })();
      })
    );
  }

  try {
    const launch = await App.getLaunchUrl();
    if (!openedOnce && launch?.url && isLikelyDocumentUri(launch.url)) {
      await openFromFilesystemUri(launch.url, onOpen);
    }
  } catch {
    /* ignore */
  }

  handles.push(
    await App.addListener("appUrlOpen", ({ url }) => {
      if (!url || !isLikelyDocumentUri(url)) return;
      void openFromFilesystemUri(url, onOpen);
    })
  );

  return () => {
    for (const h of handles) void h.remove();
  };
}
